// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-22) — the Earth close up in the headset, while the
 * right stick zooms in (vrFocusZoom.ts). The headset's Earth is one 8192 ×
 * 4096 picture, about 4.9 km a pixel; past a few times magnified it blurs.
 * Around the place in focus this draws three windows of 8 × 8 Web Mercator
 * tiles (256 px each, so 2048 px a side), three levels apart, finest first:
 *
 * - NASA GIBS Blue Marble (levels 0–8), the browser globe's own tiles, from
 *   AYNI's tile route (`/api/tile`);
 * - over a city pack, its aerial photos on top (levels 9–15, 16–17 in its
 *   deep box), from AYNI's map route (`/api/map/city/<pack>/photo`), as the
 *   browser's city close-up draws them (mapRenderer.ts, cityCloseup.ts).
 *
 * A level deeper than a source has is cut from its deepest tile. The shader
 * reads the windows through `DETAIL_TILES_GLSL`; a window's uniform changes
 * only when its picture is uploaded, so the two never disagree. Only the
 * Earth uses these tiles: a dataset's own pixels never do.
 */

import type * as THREE from 'three'
import { CITY_PACKS, type CityPack } from './cityCloseup'
import { detailTileUniforms } from './vrFocusZoom'

/** Tiles a window has on a side. */
export const WINDOW_TILES = 8
const TILE_PX = 256
const LEVEL_STEP = 3
const LEVELS = 3
/** The 8K picture is about level 5; a window coarser than this adds nothing. */
export const MIN_WINDOW_ZOOM = 6
/** GIBS Blue Marble's deepest level (mapRenderer's GIBS_MAX_ZOOM). */
const GIBS_MAX_ZOOM = 8
/** City photos are drawn from this level (the browser fades them in by 9). */
const PHOTO_MIN_ZOOM = 9
const PHOTO_PACK_MAX_ZOOM = 15
/** Bitmaps kept. Each is 256 KB decoded. */
const CACHE_SIZE = 384
const MAX_IN_FLIGHT = 8
/** Shortest time between two uploads of one window, ms (a 2048² upload is not free on a Quest). */
const UPLOAD_EVERY_MS = 200
/** How far the focus may drift from a window's middle, in tiles, before the window follows. */
const RECENTRE_TILES = 1.5

const GIBS_URL = (z: number, x: number, y: number) =>
  `/api/tile/BlueMarble_NextGeneration/default/2004-08/GoogleMapsCompatible_Level8/${z}/${y}/${x}.jpg`
const PHOTO_URL = (pack: string, z: number, x: number, y: number) =>
  `/api/map/city/${pack}/photo/${z}/${x}/${y}.webp`

/** Web Mercator tile coordinates (fractional) of a place at a level. */
export function tileXY(lat: number, lon: number, z: number): { x: number; y: number } {
  const n = 2 ** z
  const la = Math.max(-85.05112878, Math.min(85.05112878, lat)) * Math.PI / 180
  return {
    x: ((lon + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(la) + 1 / Math.cos(la)) / Math.PI) / 2) * n,
  }
}

/** A tile's box: west, south, east, north in degrees. */
export function tileBounds(z: number, x: number, y: number): [number, number, number, number] {
  const n = 2 ** z
  const lat = (t: number) => Math.atan(Math.sinh(Math.PI * (1 - (2 * t) / n))) * 180 / Math.PI
  return [(x / n) * 360 - 180, lat(y + 1), ((x + 1) / n) * 360 - 180, lat(y)]
}

function overlaps(a: readonly number[], b: readonly number[]): boolean {
  return a[0]! < b[2]! && a[2]! > b[0]! && a[1]! < b[3]! && a[3]! > b[1]!
}

/** The deepest tile level the Earth has at a place: 17 in a deep box, 15 in a pack, else GIBS's 8. */
export function maxTileZoomAt(lat: number, lon: number): number {
  let z = GIBS_MAX_ZOOM
  for (const pack of CITY_PACKS) {
    const [w, s, e, n] = pack.bounds
    if (lon < w || lon > e || lat < s || lat > n) continue
    z = Math.max(z, PHOTO_PACK_MAX_ZOOM)
    const deep = pack.deep?.bounds
    if (deep && lon >= deep[0] && lon <= deep[2] && lat >= deep[1] && lat <= deep[3]) z = Math.max(z, pack.deep!.maxzoom)
  }
  return z
}

/**
 * The tile level whose pixels match what the viewer sees at the focus: one
 * tile pixel to one headset pixel (`pxRad` radians of view, about 1/25°),
 * with the globe `worldRadius` metres across its radius, seen from
 * `distance` metres, magnified `m` times.
 */
export function detailTileZoom(lat: number, m: number, worldRadius: number, distance: number, pxRad = Math.PI / 180 / 25): number {
  const sourceRadPerPx = (pxRad * Math.max(0.05, distance)) / Math.max(1e-6, worldRadius * m)
  const z = Math.log2((2 * Math.PI * Math.cos(lat * Math.PI / 180)) / (TILE_PX * sourceRadPerPx))
  return Math.ceil(z - 0.25)
}

/**
 * The window's uniform: its west longitude, its north edge as Web Mercator
 * y (radians), windows per degree and windows per Mercator unit.
 */
export function windowUniform(z: number, x0: number, y0: number): [number, number, number, number] {
  const n = 2 ** z
  return [
    (x0 / n) * 360 - 180,
    Math.PI * (1 - (2 * y0) / n),
    n / (360 * WINDOW_TILES),
    n / (2 * Math.PI * WINDOW_TILES),
  ]
}

/** The window's top-left tile for a focus, kept where it was while the focus stays near its middle. */
export function windowOrigin(
  lat: number,
  lon: number,
  z: number,
  prev: { z: number; x0: number; y0: number } | null,
): { x0: number; y0: number } {
  const { x, y } = tileXY(lat, lon, z)
  const n = 2 ** z
  const half = WINDOW_TILES / 2
  if (prev && prev.z === z) {
    let dx = x - (prev.x0 + half)
    dx -= n * Math.round(dx / n)
    const dy = y - (prev.y0 + half)
    if (Math.abs(dx) <= RECENTRE_TILES && Math.abs(dy) <= RECENTRE_TILES) return { x0: prev.x0, y0: prev.y0 }
  }
  const y0 = Math.max(0, Math.min(n - WINDOW_TILES, Math.round(y - half)))
  return { x0: Math.round(x - half), y0 }
}

/** One picture a cell is drawn from: a tile, and the part of it the cell covers. */
export interface CellSource {
  url: string
  /** Source rectangle in the tile's pixels. */
  sx: number
  sy: number
  size: number
  /** The pack whose photo it is (its credit), if one. */
  pack?: CityPack
}

/** What a tile of the window is drawn from, bottom first: GIBS, then a pack's photo. */
export function cellSources(z: number, x: number, y: number): CellSource[] {
  const crop = (sz: number): { x: number; y: number; sx: number; sy: number; size: number } => {
    const f = 2 ** (z - sz)
    const size = TILE_PX / f
    return { x: Math.floor(x / f), y: Math.floor(y / f), sx: (x % f) * size, sy: (y % f) * size, size }
  }
  const out: CellSource[] = []
  const gz = Math.min(z, GIBS_MAX_ZOOM)
  const g = crop(gz)
  out.push({ url: GIBS_URL(gz, g.x, g.y), sx: g.sx, sy: g.sy, size: g.size })
  if (z >= PHOTO_MIN_ZOOM) {
    const box = tileBounds(z, x, y)
    for (const pack of CITY_PACKS) {
      if (!overlaps(box, pack.bounds)) continue
      const deep = pack.deep && overlaps(box, pack.deep.bounds) ? pack.deep.maxzoom : PHOTO_PACK_MAX_ZOOM
      const pz = Math.min(z, deep)
      const p = crop(pz)
      out.push({ url: PHOTO_URL(pack.id, pz, p.x, p.y), sx: p.sx, sy: p.sy, size: p.size, pack })
    }
  }
  return out
}

export interface DetailView {
  /** The place in focus. */
  lat: number
  lon: number
  /** The finest window's level ({@link detailTileZoom}, capped at {@link maxTileZoomAt}). */
  tileZoom: number
}

export interface VrDetailTilesHandle {
  /** Follow the place in focus, or stop drawing the windows (null). Call every frame. */
  update(view: DetailView | null, nowMs: number): void
  /** The credits of the photos the windows show now. */
  credits(): string[]
  dispose(): void
}

type CacheEntry = ImageBitmap | 'pending' | 'missing'

async function fetchBitmap(url: string): Promise<ImageBitmap | null> {
  const res = await fetch(url)
  if (!res.ok) return null
  return createImageBitmap(await res.blob())
}

export function createVrDetailTiles(
  THREE_: typeof THREE,
  load: (url: string) => Promise<ImageBitmap | null> = fetchBitmap,
): VrDetailTilesHandle {
  const cache = new Map<string, CacheEntry>()
  const queue: string[] = []
  let inFlight = 0
  let disposed = false

  interface Level {
    canvas: HTMLCanvasElement
    ctx: CanvasRenderingContext2D
    texture: THREE.CanvasTexture
    uniform: { value: number[] }
    /** What the canvas holds: level and top-left tile, or null when empty. */
    drawn: { z: number; x0: number; y0: number } | null
    /** Per cell, the sources drawn into it (their URLs joined). */
    cells: string[]
    /** The canvas changed since the last upload. */
    dirty: boolean
    lastUpload: number
    /** The window the uploaded picture shows, as its uniform. */
    shown: [number, number, number, number]
  }

  const uniforms = [detailTileUniforms.uDetailWin0, detailTileUniforms.uDetailWin1, detailTileUniforms.uDetailWin2]
  const levels: Level[] = []
  for (let i = 0; i < LEVELS; i++) {
    const canvas = document.createElement('canvas')
    canvas.width = WINDOW_TILES * TILE_PX
    canvas.height = WINDOW_TILES * TILE_PX
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('[vrDetailTiles] 2D canvas context unavailable')
    ctx.imageSmoothingQuality = 'high'
    const texture = new THREE_.CanvasTexture(canvas)
    texture.colorSpace = THREE_.SRGBColorSpace
    texture.minFilter = THREE_.LinearMipmapLinearFilter
    texture.magFilter = THREE_.LinearFilter
    texture.anisotropy = 4
    texture.generateMipmaps = true
    levels.push({
      canvas, ctx, texture, uniform: uniforms[i]!, drawn: null,
      cells: new Array(WINDOW_TILES * WINDOW_TILES).fill(''),
      dirty: false, lastUpload: -Infinity, shown: [0, 0, 0, 0],
    })
  }
  detailTileUniforms.uDetail0.value = levels[0]!.texture
  detailTileUniforms.uDetail1.value = levels[1]!.texture
  detailTileUniforms.uDetail2.value = levels[2]!.texture

  let lastView: DetailView | null = null
  /** Credits of the packs drawn in the two finest windows. */
  let shownCredits: string[] = []

  function touch(url: string, entry: CacheEntry): void {
    cache.delete(url)
    cache.set(url, entry)
  }

  function evict(): void {
    if (cache.size <= CACHE_SIZE) return
    for (const [url, entry] of cache) {
      if (cache.size <= CACHE_SIZE) break
      if (entry === 'pending') continue
      if (entry !== 'missing') entry.close()
      cache.delete(url)
    }
  }

  function pump(): void {
    while (!disposed && inFlight < MAX_IN_FLIGHT && queue.length) {
      const url = queue.shift()!
      if (cache.get(url) !== 'pending') continue
      inFlight++
      load(url)
        .catch(() => null)
        .then((bitmap) => {
          inFlight--
          if (disposed) {
            bitmap?.close()
            return
          }
          touch(url, bitmap ?? 'missing')
          evict()
          redraw()
          pump()
        })
    }
  }

  function request(url: string): void {
    if (cache.has(url)) return
    cache.set(url, 'pending')
    queue.push(url)
  }

  /** Draw every cell whose available pictures changed; ask for the missing ones, nearest first. */
  function redraw(): void {
    const credits = new Set<string>()
    const wanted: Array<{ url: string; rank: number }> = []
    levels.forEach((level, li) => {
      const d = level.drawn
      if (!d) return
      const n = 2 ** d.z
      for (let j = 0; j < WINDOW_TILES; j++) {
        for (let i = 0; i < WINDOW_TILES; i++) {
          const x = (((d.x0 + i) % n) + n) % n
          const y = d.y0 + j
          const sources = cellSources(d.z, x, y)
          const have: CellSource[] = []
          for (const s of sources) {
            const entry = cache.get(s.url)
            if (entry === undefined) {
              const dx = i + 0.5 - WINDOW_TILES / 2
              const dy = j + 0.5 - WINDOW_TILES / 2
              wanted.push({ url: s.url, rank: li * 100 + Math.hypot(dx, dy) })
            } else if (entry !== 'pending' && entry !== 'missing') {
              have.push(s)
            }
          }
          if (li < 2) for (const s of have) if (s.pack) credits.add(s.pack.photoCredit)
          const key = have.map((s) => s.url).join('|')
          const cell = j * WINDOW_TILES + i
          if (level.cells[cell] === key) continue
          level.cells[cell] = key
          const dx = i * TILE_PX
          const dy = j * TILE_PX
          level.ctx.clearRect(dx, dy, TILE_PX, TILE_PX)
          for (const s of have) {
            const bitmap = cache.get(s.url) as ImageBitmap
            touch(s.url, bitmap)
            level.ctx.drawImage(bitmap, s.sx, s.sy, s.size, s.size, dx, dy, TILE_PX, TILE_PX)
          }
          level.dirty = true
        }
      }
    })
    shownCredits = [...credits]
    wanted.sort((a, b) => a.rank - b.rank)
    for (const w of wanted) request(w.url)
    pump()
  }

  function place(level: Level, z: number, origin: { x0: number; y0: number } | null): boolean {
    const d = level.drawn
    if (!origin) {
      if (!d) return false
      level.drawn = null
      level.cells.fill('')
      level.ctx.clearRect(0, 0, level.canvas.width, level.canvas.height)
      level.dirty = true
      return true
    }
    if (d && d.z === z && d.x0 === origin.x0 && d.y0 === origin.y0) return false
    level.drawn = { z, x0: origin.x0, y0: origin.y0 }
    level.cells.fill('')
    level.ctx.clearRect(0, 0, level.canvas.width, level.canvas.height)
    level.dirty = true
    return true
  }

  return {
    update(view, nowMs) {
      if (disposed) return
      if (!view) {
        detailTileUniforms.uDetailOn.value = 0
        lastView = null
        return
      }
      let moved = false
      if (
        !lastView || lastView.tileZoom !== view.tileZoom ||
        Math.abs(lastView.lat - view.lat) > 1e-9 || Math.abs(lastView.lon - view.lon) > 1e-9
      ) {
        levels.forEach((level, li) => {
          const z = view.tileZoom - li * LEVEL_STEP
          const origin = z >= MIN_WINDOW_ZOOM ? windowOrigin(view.lat, view.lon, z, level.drawn) : null
          if (place(level, z, origin)) moved = true
        })
        lastView = { ...view }
      }
      if (moved) redraw()
      // The uniforms are the page's: say again each frame which pictures and
      // windows are this handle's (a second handle, say in a check, may have
      // pointed them elsewhere).
      detailTileUniforms.uDetail0.value = levels[0]!.texture
      detailTileUniforms.uDetail1.value = levels[1]!.texture
      detailTileUniforms.uDetail2.value = levels[2]!.texture
      let any = false
      for (const level of levels) {
        if (level.dirty && nowMs - level.lastUpload >= UPLOAD_EVERY_MS) {
          level.dirty = false
          level.lastUpload = nowMs
          level.texture.needsUpdate = true
          level.shown = level.drawn ? windowUniform(level.drawn.z, level.drawn.x0, level.drawn.y0) : [0, 0, 0, 0]
        }
        level.uniform.value = level.shown
        if (level.shown[2] > 0) any = true
      }
      detailTileUniforms.uDetailOn.value = any ? 1 : 0
    },

    credits() {
      return detailTileUniforms.uDetailOn.value ? shownCredits : []
    },

    dispose() {
      disposed = true
      detailTileUniforms.uDetailOn.value = 0
      for (const [i, level] of levels.entries()) {
        level.texture.dispose()
        uniforms[i]!.value = [0, 0, 0, 0]
      }
      detailTileUniforms.uDetail0.value = null
      detailTileUniforms.uDetail1.value = null
      detailTileUniforms.uDetail2.value = null
      for (const entry of cache.values()) if (entry !== 'pending' && entry !== 'missing') entry.close()
      cache.clear()
      queue.length = 0
    },
  }
}
